import { Box, Button, Typography, useTheme } from "@material-ui/core";
import { RouteComponentProps } from "@reach/router";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import useAvailableMemberships from "../../../hooks/use-available-memberships";
import useAuth from "../../../hooks/use-auth";
import {
  DebitPaymentAccountType,
  MembershipSubscriptionType,
  PaymentType
} from "../../../types/airgraft";
import CreditPaymentForm from "../CreditPaymentForm";
import OrDivider from "../../or-divider";
import AeropayButton from "../DebitPaymentForm/AeropayButton";
import {
  getMembershipShortDescription,
  getMembershipDetailedDescription,
  getMembershipName,
  getMembershipRecurringPrice,
  activateMembershipSubscriptionWithDetails,
  activateMembershipSubscriptionWithPaymentMethod
} from "../../../services/membership";
import { Alert, Skeleton } from "@material-ui/lab";
import { formatPrice } from "../../../helpers/price";
import { PaymentLoadingOverlay } from "../../loading-overlay";
import { analyticsTrackEvent } from "../../../services/analytics";
import usePaymentMethods from "../../../hooks/use-payment-methods";
import AlreadyMembershipSubscription from "../../../containers/authentication/components/AlreadyMembershipSubscription";
import { getAvailableDefaultPayment } from "../../../containers/user/utils";
import { reportSentryClientError } from "../../../services/sentry";
import useTranslate from "../../../hooks/use-translate";
import TosField from "../TosField";
import { useQueryClient } from "react-query";

type Props = RouteComponentProps<{ podKeyOrSerialNumber: string }> & {
  onSubscription: () => void;
  titleComponent?: any;
  labels?: {
    title?: string;
  };
};

/**
 * Membership payemnt screen. Let user select a available membership and enter payemnt informations.
 * Used by activation & referral .
 */
export default function MembershipPaymentForm({
  onSubscription,
  titleComponent: customTitleComponent,
  labels
}: Props) {
  const theme = useTheme();
  const t = useTranslate();
  const queryClient = useQueryClient();

  /**
   * User
   */
  const {
    updateMembershipSubscription,
    membershipSubscription,
    isLoadingMembershipSubscription,
    lastExpiredMembershipSubscriptionReason,
    refetchMembershipSubscription
  } = useAuth();

  /**
   * Payment methods
   */
  const {
    data: paymentMethods,
    isLoading: isLoadingPaymentMethods,
    refetch: refetchPaymentMethods
  } = usePaymentMethods();
  const availableDefaultPayment = paymentMethods
    ? getAvailableDefaultPayment(paymentMethods)
    : null;

  /**
   * Available Memberships
   */
  const {
    data: availableMemberships,
    isLoading: isLoadingAvailableMemberships,
    error: availableMembershipsError
  } = useAvailableMemberships();

  /**
   * TOS & Payment type selection
   */
  const [isTosAccepted, setTosAccepted] = useState(false);
  const [selectedPaymentType, setSelectedPaymentType] =
    useState<PaymentType>(null);

  /**
   * Submitting overlay
   */
  const [isSubmitting, setSubmitting] = useState(false);

  /**
   * Register a form for the membership radio options
   * The values will be used during the VGS submit callback to know which membership the user selected
   */
  const { register, watch, setValue } = useForm<{ membership: string }>({
    shouldUnregister: false
  });
  const selectedMembershipId = watch("membership");
  const selectedMembership = selectedMembershipId
    ? availableMemberships.find((m) => m.id === selectedMembershipId)
    : null;

  /**
   * After loading membership, pre-select first membership
   */
  useEffect(() => {
    if (availableMemberships && !selectedMembership) {
      setValue("membership", availableMemberships[0].id);
    }
  }, [availableMemberships, selectedMembership]);

  /**
   * Detect if we can propose the user to "Resume" his expired subscription using the default payment method.
   */
  const isMembershipSubscriptionResumable =
    membershipSubscription === false &&
    !!availableDefaultPayment &&
    availableMemberships?.length > 0;
  const resumableMembership = availableMemberships
    ? availableMemberships[0]
    : null;
  const [resumeMembershipError, setResumeMembershipError] = useState(null);
  const [isUsingAnotherCardToResume, setUsingAnotherCardToResume] =
    useState(false);

  /**
   * Resmuse membership using existing payment method
   */
  const handleResumeMembershipClick = async () => {
    if (!isMembershipSubscriptionResumable) {
      return;
    }
    setResumeMembershipError(null);
    setSubmitting(true);

    try {
      await activateMembershipSubscriptionWithPaymentMethod({
        membershipId: resumableMembership.id,
        membershipPrice: resumableMembership.price,
        paymentMethod: availableDefaultPayment
      });
    } catch (e) {
      if (e?.message) {
        setResumeMembershipError(e.message);
      } else {
        reportSentryClientError(
          new Error(
            "Something went wrong while resuming membership subscription."
          ),
          { response: e }
        );
        setResumeMembershipError(t("unknownErrorMessage"));
      }
      setSubmitting(false);
      return;
    }

    // After membership was resume, refresh membership subscription and redirect
    await refetchMembershipSubscription();
    onSubscription();
  };

  /**
   * After payment has been submit to the API
   */
  const handleCreditCardFormSubmit = async (response: any) => {
    updateMembershipSubscription(
      response.membershipSubscription as MembershipSubscriptionType
    );
    await refetchPaymentMethods();

    analyticsTrackEvent(
      `membership.payment.start${hasSelectedTrial ? ".trial" : ""}`,
      {
        paymentMethod: "credit-card",
        price: selectedMembership.price
      }
    );

    onSubscription();
  };
  const handleBeforeSubmit = () => setSubmitting(true);
  const handleSubmitFail = () => {
    setSubmitting(false);
  };

  /**
   * Additional payload values sent to API when submitting payment
   */
  const mapCreditFormSubmitData = (cardInfo) => {
    return {
      last4: cardInfo.last4,
      cardType: cardInfo.cardType,
      membershipId: selectedMembershipId,
      chargeAmount: selectedMembership?.price
    };
  };

  /**
   * After user select a debit bank account inside Aeropay button
   */
  const handleAeopayBankAccountSubmit = async (
    debitBankAccount: DebitPaymentAccountType
  ) => {
    setSubmitting(true);

    try {
      await activateMembershipSubscriptionWithDetails({
        membershipId: selectedMembershipId,
        membershipPrice: selectedMembership?.price,
        bankAccountDetailsId: debitBankAccount.id
      });
    } catch (e) {
      setSubmitting(false);
    }

    // After membership was activated using debit account, refresh membership subscription and redirect to next step
    queryClient.invalidateQueries("user-payment-methods");
    await refetchMembershipSubscription();
    onSubscription();
  };

  /**
   * Render variables
   */
  const onlyOneMembershipOption =
    availableMemberships && availableMemberships.length === 1; // If there is only one membership option possible, hide radio input
  const hasSelectedTrial = selectedMembership?.trialDuration > 0;

  if (availableMembershipsError) {
    return (
      <Box mt={{ xs: 2, sm: 4 }} display="flex" flexDirection="column">
        <Alert severity="error">
          Something went wrong trying to load the payment form. Try reloading
          the page.
        </Alert>

        <Box mt={2}>
          <Button
            variant="contained"
            onClick={() => window.location.reload()}
            fullWidth
          >
            Reload
          </Button>
        </Box>
      </Box>
    );
  }

  // Show skeletons while loading required data
  if (
    isLoadingAvailableMemberships ||
    isLoadingMembershipSubscription ||
    isLoadingPaymentMethods
  ) {
    return (
      <Box mt={{ xs: 2, sm: 4 }}>
        <Skeleton width="50%" height="32px" animation="wave" />
        <Skeleton width="100%" height="32px" animation="wave" />
        <Skeleton width="60%" height="32px" animation="wave" />
      </Box>
    );
  }

  // User already has a membership
  if (membershipSubscription && !isSubmitting) {
    return (
      <AlreadyMembershipSubscription onContinue={() => onSubscription()} />
    );
  }

  // Use custom title component if specified, else fallback to h5
  const TitleComponent = customTitleComponent
    ? customTitleComponent
    : ({ children }) => <Typography variant="h5">{children}</Typography>;

  // Resume membership
  if (isMembershipSubscriptionResumable && !isUsingAnotherCardToResume) {
    let titles = [
      "Airgraft Membership is required to unlock this pod.",
      "It lets you unlock unlimited pods and saves you money on each one. "
    ];
    if (
      lastExpiredMembershipSubscriptionReason ===
      "TRIAL_POD_ACTIVATION_LIMIT_REACHED"
    ) {
      titles = [
        "You’ve already unlocked one pod included in the free trial.",
        "Airgraft Membership is required to unlock this pod.",
        "It lets you unlock unlimited pods and saves you money on each one. "
      ];
    }

    return (
      <>
        <div>
          {/* Title */}
          {titles.map((title) => (
            <Box mb={{ xs: 1, md: 2 }} key={title}>
              <TitleComponent>{title}</TitleComponent>
            </Box>
          ))}

          {/* Membership card */}
          <Box mt={{ xs: 4, md: 4 }}>
            <Box
              borderLeft={`8px solid ${theme.palette.primary.main}`}
              bgcolor="common.white"
              boxShadow="0px 4px 4px rgba(0, 0, 0, 0.05)"
              borderRadius="4px"
              padding={2}
              paddingLeft={1}
              display="flex"
              alignItems="center"
              width="100%"
              component="label"
              marginBottom={1}
            >
              <Box
                display="flex"
                flexDirection="column"
                flex={1}
                marginLeft={1}
              >
                <Box display="flex" justifyContent="space-between">
                  <Typography>
                    {getMembershipName(resumableMembership)}
                  </Typography>
                  <Typography>
                    {getMembershipRecurringPrice(resumableMembership)}
                  </Typography>
                </Box>
                <Typography
                  color="textSecondary"
                  variant="body2"
                  dangerouslySetInnerHTML={{
                    __html: getMembershipShortDescription(resumableMembership)
                  }}
                />
              </Box>
            </Box>
          </Box>

          {/* Total/Price */}
          <Box display="flex" flexDirection="column" width="100%" marginTop={2}>
            <Box display="flex" justifyContent="space-between" mb={0.5}>
              <Typography className="bold">Total due today</Typography>
              <Box display="flex" alignItems="baseline">
                <Typography className="bold">
                  {formatPrice(
                    {
                      amount: resumableMembership?.price,
                      currencyCode: "USD"
                    },
                    { hideCurrencyCode: true }
                  )}
                </Typography>
                <Typography
                  variant="caption"
                  style={{ marginLeft: theme.spacing(0.5) }}
                >
                  (Tax included)
                </Typography>
              </Box>
            </Box>
          </Box>
        </div>

        <Box mt={{ xs: 4, md: 8 }}>
          {resumeMembershipError && (
            <Box mb={{ xs: 4, md: 4 }}>
              <Alert severity="error" icon={false}>
                {resumeMembershipError}
              </Alert>

              <Box mt={2}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => setUsingAnotherCardToResume(true)}
                >
                  Use another card
                </Button>
              </Box>
            </Box>
          )}

          <Button
            variant="contained"
            color="primary"
            fullWidth
            onClick={handleResumeMembershipClick}
          >
            {lastExpiredMembershipSubscriptionReason ===
            "TRIAL_POD_ACTIVATION_LIMIT_REACHED"
              ? "Start"
              : "Remuse"}{" "}
            membership
          </Button>
        </Box>

        {isSubmitting && <PaymentLoadingOverlay />}
      </>
    );
  }

  // Membership payment form
  return (
    <>
      <TitleComponent>{labels?.title || "Your payment details"}</TitleComponent>

      <Box mt={3}>
        <Box marginBottom={4}>
          {/* List of available memberships */}
          {(availableMemberships || []).map((membership) => (
            // Membership card
            <Box
              key={membership.id}
              borderLeft={`8px solid ${
                selectedMembershipId === membership.id
                  ? theme.palette.primary.main
                  : theme.palette.common.white
              }`}
              bgcolor="common.white"
              boxShadow="0px 4px 4px rgba(0, 0, 0, 0.05)"
              borderRadius="4px"
              padding={2}
              paddingLeft={1}
              display="flex"
              alignItems="center"
              width="100%"
              component="label"
              marginBottom={1}
              // @ts-ignore
              htmlFor={membership.id}
            >
              <input
                type="radio"
                name="membership"
                id={membership.id}
                value={membership.id}
                ref={register({ required: true })}
                style={{
                  display: onlyOneMembershipOption ? "none" : "block"
                }}
              />
              <Box
                display="flex"
                flexDirection="column"
                flex={1}
                marginLeft={1}
              >
                <Box display="flex" justifyContent="space-between">
                  <Typography>{getMembershipName(membership)}</Typography>
                  <Typography>
                    {getMembershipRecurringPrice(membership)}
                  </Typography>
                </Box>
                <Typography
                  color="textSecondary"
                  variant="body2"
                  dangerouslySetInnerHTML={{
                    __html: getMembershipShortDescription(membership)
                  }}
                />
              </Box>
            </Box>
          ))}

          {/* Pricing information */}
          {hasSelectedTrial && (
            <Box mt={1}>
              <Typography color="textSecondary" variant="body2">
                Your {selectedMembership.trialDuration} day free trial will
                start when you submit your payment details.
              </Typography>
            </Box>
          )}

          {/* Price Summary */}
          <Box display="flex" flexDirection="column" width="100%" marginTop={2}>
            <Box display="flex" justifyContent="space-between" mb={0.5}>
              <Typography className="bold">Total due today</Typography>
              <Box display="flex" alignItems="baseline">
                <Typography className="bold">
                  {hasSelectedTrial && !isUsingAnotherCardToResume
                    ? "$0.00"
                    : formatPrice(
                        {
                          amount: selectedMembership?.price,
                          currencyCode: "USD"
                        },
                        { hideCurrencyCode: true }
                      )}
                </Typography>
                {!hasSelectedTrial && (
                  <Typography
                    variant="caption"
                    style={{ marginLeft: theme.spacing(0.5) }}
                  >
                    (Tax included)
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Payment type selector */}
        {!selectedPaymentType && (
          <Box
            pt={{ xs: 1, sm: 2 }}
            borderTop={`1px solid ${theme.palette.grey[400]}`}
          >
            <TosField value={isTosAccepted} onChange={setTosAccepted} />

            <Box mt={3}>
              <Button
                variant="contained"
                fullWidth
                disabled={!isTosAccepted}
                onClick={() => setSelectedPaymentType("CREDIT")}
              >
                Pay with credit card
              </Button>

              <Box marginY={1}>
                <OrDivider />
              </Box>

              <AeropayButton
                onDebitBankAccountAdded={handleAeopayBankAccountSubmit}
                disabled={!isTosAccepted}
              />
            </Box>
          </Box>
        )}

        {/* Credit card form */}
        {selectedPaymentType === "CREDIT" && (
          <CreditPaymentForm
            submitEndpoint="/mps/membership-service/subscribe"
            mapSubmitData={mapCreditFormSubmitData}
            onSubmit={handleCreditCardFormSubmit}
            onBeforeSubmit={handleBeforeSubmit}
            onSubmitFail={handleSubmitFail}
            submitLabel="Start Membership"
          />
        )}
      </Box>

      {/* Detailed description of the selected membership */}
      {selectedMembership && (
        <Box marginTop={3}>
          <Typography variant="body2" className="center">
            {getMembershipDetailedDescription(selectedMembership)}
          </Typography>
        </Box>
      )}

      {selectedPaymentType === "CREDIT" && (
        <Box mt={2} display="flex" justifyContent="center">
          <Button
            variant="text"
            size="small"
            onClick={() => setSelectedPaymentType(null)}
          >
            Use another payment method
          </Button>
        </Box>
      )}

      {/* Submitting overlay */}
      {isSubmitting && <PaymentLoadingOverlay />}
    </>
  );
}
